import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './AdvertisersPage.scss';

const API_BASE_URL = 'https://api.display-promo.ru';

const formatTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

const AdvertisersPage = () => {
    const { hash } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [draftFrom, setDraftFrom] = useState('');
    const [draftTo, setDraftTo] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [onlineOnly, setOnlineOnly] = useState(false);
    const [hasReportOnly, setHasReportOnly] = useState(false);
    const [sortBy, setSortBy] = useState('lastPlayDesc');

    const [modalMedia, setModalMedia] = useState(null);

    useEffect(() => {
        if (!hash) return;

        const source = axios.CancelToken.source();

        setLoading(true);
        setError(null);

        axios
            .get(`${API_BASE_URL}/api/advertisers/${hash}`, {
                cancelToken: source.token,
                params: {
                    from: dateFrom || undefined,
                    to: dateTo || undefined,
                },
            })
            .then((response) => {
                setData(response.data);

                if (!dateFrom && !dateTo && response.data.date) {
                    const d = response.data.date;
                    setDraftFrom(d);
                    setDraftTo(d);
                }
            })
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                const message =
                    e.response?.data?.error ||
                    e.response?.data?.message ||
                    e.message ||
                    'Ошибка запроса';
                setError(message);
            })
            .finally(() => {
                setLoading(false);
            });

        return () => {
            source.cancel('Component unmounted');
        };
    }, [hash, dateFrom, dateTo]);

    useEffect(() => {
        if (!modalMedia) return;

        const handleKey = (e) => {
            if (e.key === 'Escape') {
                setModalMedia(null);
            }
            if (e.key === 'ArrowLeft') {
                setModalMedia((prev) => {
                    if (!prev || !prev.reports || prev.reports.length === 0) return prev;
                    const len = prev.reports.length;
                    const nextIndex = (prev.index - 1 + len) % len;
                    return { ...prev, index: nextIndex };
                });
            }
            if (e.key === 'ArrowRight') {
                setModalMedia((prev) => {
                    if (!prev || !prev.reports || prev.reports.length === 0) return prev;
                    const len = prev.reports.length;
                    const nextIndex = (prev.index + 1) % len;
                    return { ...prev, index: nextIndex };
                });
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [modalMedia]);

    const dateLabel = data?.date
        ? new Date(data.date).toLocaleDateString('ru-RU')
        : null;

    const campaignTitle = data?.adFile?.title || 'Компания';

    const placementsRaw = data?.placements || [];

    // Группируем размещения по адресу+городу, чтобы убрать дубли
    const groupedPlacementsRaw = useMemo(() => {
        const map = new Map();

        placementsRaw.forEach((p) => {
            const addr = p.screenAddress || '';
            const city = p.city || '';
            const key = `${addr}|||${city}`;

            const existing = map.get(key);
            if (!existing) {
                map.set(key, {
                    ...p,
                    reports: [...(p.reports || [])],
                    plays: [...(p.plays || [])],
                });
            } else {
                // онлайн — если хотя бы один из дубликатов онлайн
                existing.online = existing.online || p.online;

                // lastActive = самая свежая
                if (p.lastActive) {
                    if (!existing.lastActive) {
                        existing.lastActive = p.lastActive;
                    } else {
                        const ex = new Date(existing.lastActive).getTime();
                        const cur = new Date(p.lastActive).getTime();
                        if (!Number.isNaN(cur) && cur > ex) {
                            existing.lastActive = p.lastActive;
                        }
                    }
                }

                // lastPlayAt = самая свежая
                if (p.lastPlayAt) {
                    if (!existing.lastPlayAt) {
                        existing.lastPlayAt = p.lastPlayAt;
                    } else {
                        const ex = new Date(existing.lastPlayAt).getTime();
                        const cur = new Date(p.lastPlayAt).getTime();
                        if (!Number.isNaN(cur) && cur > ex) {
                            existing.lastPlayAt = p.lastPlayAt;
                        }
                    }
                }

                // объединяем отчёты и показы
                existing.reports = [
                    ...existing.reports,
                    ...(p.reports || []),
                ];
                existing.plays = [
                    ...existing.plays,
                    ...(p.plays || []),
                ];
            }
        });

        // опционально можно отсортировать внутри групп по дате
        const result = Array.from(map.values());

        result.forEach((item) => {
            if (item.reports?.length) {
                item.reports = [...item.reports].sort((a, b) => {
                    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return tb - ta;
                });
            }
            if (item.plays?.length) {
                item.plays = [...item.plays].sort((a, b) => {
                    const ta = a.playedAt ? new Date(a.playedAt).getTime() : 0;
                    const tb = b.playedAt ? new Date(b.playedAt).getTime() : 0;
                    return tb - ta;
                });
            }
        });

        return result;
    }, [placementsRaw]);

    const analytics = useMemo(() => {
        const totalScreens = groupedPlacementsRaw.length;
        const activeScreens = groupedPlacementsRaw.filter((p) => p.online).length;
        const withReports = groupedPlacementsRaw.filter(
            (p) => (p.reports || []).length > 0,
        ).length;

        return {
            totalScreens,
            activeScreens,
            withReports,
        };
    }, [groupedPlacementsRaw]);

    const placements = useMemo(() => {
        let list = groupedPlacementsRaw.slice();

        if (searchQuery.trim() !== '') {
            const q = searchQuery.trim().toLowerCase();
            list = list.filter((p) => {
                const address = (p.screenAddress || '').toLowerCase();
                const city = (p.city || '').toLowerCase();
                const title = (p.screenTitle || '').toLowerCase();
                return (
                    address.includes(q) ||
                    city.includes(q) ||
                    title.includes(q)
                );
            });
        }

        if (onlineOnly) {
            list = list.filter((p) => p.online);
        }

        if (hasReportOnly) {
            list = list.filter((p) => (p.reports || []).length > 0);
        }

        list.sort((a, b) => {
            if (sortBy === 'city') {
                const ac = (a.city || '').toLowerCase();
                const bc = (b.city || '').toLowerCase();
                if (ac < bc) return -1;
                if (ac > bc) return 1;
                const aa = (a.screenAddress || '').toLowerCase();
                const ba = (b.screenAddress || '').toLowerCase();
                if (aa < ba) return -1;
                if (aa > ba) return 1;
                return 0;
            }

            const at = a.lastPlayAt ? new Date(a.lastPlayAt).getTime() : 0;
            const bt = b.lastPlayAt ? new Date(b.lastPlayAt).getTime() : 0;

            if (sortBy === 'lastPlayAsc') {
                return at - bt;
            }

            return bt - at;
        });

        return list;
    }, [groupedPlacementsRaw, searchQuery, onlineOnly, hasReportOnly, sortBy]);

    if (!hash) {
        return (
            <div className="advertisers-page">
                <div className="shell">
                    <header>
                        <h1>
                            Мониторинг показов
                            <span>панель рекламодателя</span>
                        </h1>
                        <p>Отсутствует идентификатор кампании в URL.</p>
                    </header>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="advertisers-page">
                <div className="shell">
                    <header>
                        <h1>
                            Мониторинг показов
                            <span>панель рекламодателя</span>
                        </h1>
                        <p>Загружаем данные по вашей кампании…</p>
                        <p className="campaign-hash">
                            ID кампании: <strong>{hash}</strong>
                        </p>
                    </header>
                    <main className="layout">
                        <section className="content">
                            <div className="content-head">
                                <div className="content-head-title">Список адресов</div>
                                <div className="content-head-meta">
                                    <span>Дата: —</span>
                                    <span className="badge">Компания загружается…</span>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="advertisers-page">
                <div className="shell">
                    <header>
                        <h1>
                            Мониторинг показов
                            <span>панель рекламодателя</span>
                        </h1>
                        <p>Ошибка загрузки данных кампании.</p>
                        <p className="campaign-hash">
                            ID кампании: <strong>{hash}</strong>
                        </p>
                    </header>
                    <main className="layout">
                        <section className="content">
                            <div className="content-head">
                                <div className="content-head-title">Список адресов</div>
                                <div className="content-head-meta">
                                    <span>Дата: —</span>
                                    <span className="badge badge-error">
                                        Ошибка: {error}
                                    </span>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        );
    }

    const handleApplyDates = () => {
        setDateFrom(draftFrom || '');
        setDateTo(draftTo || '');
    };

    const handleMediaClick = (reportIndex, placement) => {
        setModalMedia({
            placementId: placement.id,
            screenAddress: placement.screenAddress,
            city: placement.city,
            index: reportIndex,
            reports: placement.reports || [],
        });
    };

    const closeModal = () => setModalMedia(null);

    const handlePrevReport = () => {
        setModalMedia((prev) => {
            if (!prev || !prev.reports || prev.reports.length === 0) return prev;
            const len = prev.reports.length;
            const nextIndex = (prev.index - 1 + len) % len;
            return { ...prev, index: nextIndex };
        });
    };

    const handleNextReport = () => {
        setModalMedia((prev) => {
            if (!prev || !prev.reports || prev.reports.length === 0) return prev;
            const len = prev.reports.length;
            const nextIndex = (prev.index + 1) % len;
            return { ...prev, index: nextIndex };
        });
    };

    const periodFromLabel = data.from
        ? new Date(data.from).toLocaleDateString('ru-RU')
        : null;
    const periodToLabel = data.to
        ? new Date(data.to).toLocaleDateString('ru-RU')
        : null;

    const currentReport =
        modalMedia && modalMedia.reports && modalMedia.reports.length
            ? modalMedia.reports[modalMedia.index]
            : null;

    return (
        <div className="advertisers-page">
            <div className="shell">
                <header>
                    <h1>
                        Мониторинг показов
                        <span>панель рекламодателя</span>
                    </h1>
                    <p>
                        Выберите адрес экрана в списке ниже и смотрите фотоотчёт и точное
                        время запусков вашего ролика.
                    </p>
                    {hash && (
                        <p className="campaign-hash">
                            ID кампании: <strong>{hash}</strong>
                        </p>
                    )}
                </header>

                <main className="layout">
                    <section className="content">
                        <div className="content-head">
                            <div className="content-head-title">Список адресов</div>
                            <div className="content-head-meta">
                                <span>Дата: {dateLabel || '—'}</span>
                                <span className="badge">
                                    Компания: {campaignTitle}
                                    {hash ? ` (${hash})` : ''}
                                </span>
                            </div>
                        </div>

                        <div className="analytics">
                            <div className="analytics-card">
                                <div className="analytics-label">Всего экранов</div>
                                <div className="analytics-value">
                                    {analytics.totalScreens}
                                </div>
                            </div>
                            <div className="analytics-card">
                                <div className="analytics-label">Активных экранов</div>
                                <div className="analytics-value">
                                    {analytics.activeScreens}
                                </div>
                            </div>
                            <div className="analytics-card">
                                <div className="analytics-label">Экранов с отчётом</div>
                                <div className="analytics-value">
                                    {analytics.withReports}
                                </div>
                            </div>
                        </div>

                        <div className="content-filters">
                            <div className="content-filters-left">
                                <input
                                    type="text"
                                    className="filter-input"
                                    placeholder="Поиск по адресу, городу или экрану"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="content-filters-center">
                                <div className="filter-dates">
                                    <input
                                        type="date"
                                        className="filter-date"
                                        value={draftFrom}
                                        onChange={(e) => setDraftFrom(e.target.value)}
                                    />
                                    <span>—</span>
                                    <input
                                        type="date"
                                        className="filter-date"
                                        value={draftTo}
                                        onChange={(e) => setDraftTo(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="filter-apply-btn"
                                        onClick={handleApplyDates}
                                    >
                                        Показать
                                    </button>
                                </div>
                            </div>
                            <div className="content-filters-right">
                                <label className="filter-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={onlineOnly}
                                        onChange={(e) => setOnlineOnly(e.target.checked)}
                                    />
                                    <span>Только онлайн-экраны</span>
                                </label>
                                <label className="filter-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={hasReportOnly}
                                        onChange={(e) =>
                                            setHasReportOnly(e.target.checked)
                                        }
                                    />
                                    <span>Только с отчётом</span>
                                </label>
                                <select
                                    className="filter-select"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    <option value="lastPlayDesc">
                                        Сначала с последними показами
                                    </option>
                                    <option value="lastPlayAsc">
                                        Сначала ранние показы
                                    </option>
                                    <option value="city">По городу и адресу</option>
                                </select>
                            </div>
                        </div>

                        <div className="screens">
                            {placements.length === 0 && (
                                <div className="screen screen-empty">
                                    <div className="screen-body">
                                        <p>
                                            Для данной кампании по заданным фильтрам нет
                                            размещений или загруженных отчётов.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {placements.map((placement, index) => {
                                const lastPlayTime = placement.lastPlayAt
                                    ? formatTime(placement.lastPlayAt)
                                    : null;

                                const lastActiveLabel = placement.lastActive
                                    ? formatDateTime(placement.lastActive)
                                    : null;

                                const plays = placement.plays || [];
                                const reports = placement.reports || [];

                                return (
                                    <details
                                        key={placement.id || `${placement.screenAddress}-${placement.city}`}
                                        className="screen"
                                        open={index === 0}
                                    >
                                        <summary>
                                            <div className="screen-summary">
                                                <div className="screen-info">
                                                    <div className="screen-address">
                                                        <span className="screen-address-line">
                                                            {placement.screenAddress}
                                                            {placement.city
                                                                ? `, ${placement.city}`
                                                                : ''}
                                                        </span>
                                                        {lastActiveLabel && (
                                                            <span className="screen-last-active">
                                                                Последняя активность:{' '}
                                                                {lastActiveLabel}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="screen-meta">
                                                    {placement.online && (
                                                        <div className="tag tag-online">
                                                            <span className="dot-online" />
                                                            Экран онлайн
                                                        </div>
                                                    )}
                                                    {!placement.online && (
                                                        <div className="tag tag-offline">
                                                            <span className="dot-offline" />
                                                            Экран неактивен
                                                        </div>
                                                    )}
                                                    {lastPlayTime && (
                                                        <div className="tag">
                                                            Последний показ в {lastPlayTime}
                                                        </div>
                                                    )}
                                                    <div className="chevron">⌄</div>
                                                </div>
                                            </div>
                                        </summary>

                                        <div className="screen-body">
                                            <div className="section-block">
                                                <h4>
                                                    Фотоотчёт
                                                    <span>подтверждение запуска вашего ролика</span>
                                                </h4>

                                                {reports.length > 0 ? (
                                                    <div className="photo-grid">
                                                        {reports.map((report, reportIndex) => (
                                                            <article
                                                                key={report.id}
                                                                className="photo-card"
                                                                onClick={() =>
                                                                    handleMediaClick(
                                                                        reportIndex,
                                                                        placement,
                                                                    )
                                                                }
                                                            >
                                                                <div className="photo-card-inner">
                                                                    {report.type === 'video' ? (
                                                                        <video
                                                                            src={report.url}
                                                                            preload="metadata"
                                                                        />
                                                                    ) : (
                                                                        <img
                                                                            src={report.url}
                                                                            alt="Кадр фотоотчёта"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </article>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="photo-hint">
                                                        Для этого экрана ещё не загружены файлы
                                                        фото/видео отчёта.
                                                    </p>
                                                )}

                                                {reports.length > 0 && (
                                                    <p className="photo-hint">
                                                        Кликните по кадру, чтобы открыть отчёт и
                                                        пролистать кадры по этому адресу.
                                                    </p>
                                                )}
                                            </div>

                                            <div className="section-block">
                                                <h4>
                                                    Журнал показов
                                                    <span>фиксируются только показы вашего ролика</span>
                                                </h4>

                                                <div className="time-list-wrapper">
                                                    <div className="time-list-header">
                                                        <span>Время</span>
                                                        <span>Событие</span>
                                                    </div>

                                                    <div className="time-list">
                                                        {plays.length === 0 && (
                                                            <div className="time-list-row time-list-row-empty">
                                                                <span className="time-list-time">—</span>
                                                                <span className="time-list-label">
                                                                    За выбранный период не зафиксировано
                                                                    показов вашего ролика на этом адресе.
                                                                    {' '}
                                                                    Версия программного обеспечения пока
                                                                    не позволяет получать данные о времени
                                                                    показа.
                                                                </span>
                                                            </div>
                                                        )}

                                                        {plays.map((play) => (
                                                            <div
                                                                key={play.id}
                                                                className="time-list-row"
                                                            >
                                                                <span className="time-list-time">
                                                                    {formatTime(play.playedAt)}
                                                                </span>
                                                                <span className="time-list-label">
                                                                    Показ вашего ролика
                                                                    <span className="time-list-tag">
                                                                        мой ролик
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="time-list-footer">
                                                        <span>
                                                            Всего показов вашего ролика за период:{' '}
                                                            {plays.length}
                                                        </span>
                                                        <span>
                                                            Интервал журнала за выбранный период:{' '}
                                                            {periodFromLabel || '—'} – {periodToLabel || '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </details>
                                );
                            })}
                        </div>
                    </section>
                </main>
            </div>

            {modalMedia && currentReport && (
                <div className="media-modal" role="dialog" aria-modal="true">
                    <div
                        className="media-modal__backdrop"
                        onClick={closeModal}
                    />
                    <div className="media-modal__dialog">
                        <button
                            type="button"
                            className="media-modal__close"
                            onClick={closeModal}
                            aria-label="Закрыть"
                        >
                            ✕
                        </button>

                        <div className="media-modal__nav">
                            <button
                                type="button"
                                className="media-modal__nav-btn"
                                onClick={handlePrevReport}
                            >
                                ‹
                            </button>
                            <div className="media-modal__nav-info">
                                Кадр {modalMedia.index + 1} из{' '}
                                {modalMedia.reports.length}
                            </div>
                            <button
                                type="button"
                                className="media-modal__nav-btn"
                                onClick={handleNextReport}
                            >
                                ›
                            </button>
                        </div>

                        <div className="media-modal__content">
                            {currentReport.type === 'video' ? (
                                <video
                                    src={currentReport.url}
                                    controls
                                    autoPlay
                                />
                            ) : (
                                <img
                                    src={currentReport.url}
                                    alt="Медиа отчёта"
                                />
                            )}
                        </div>
                        <div className="media-modal__meta">
                            {modalMedia.screenAddress && (
                                <div className="media-modal__meta-line">
                                    <span>Адрес:</span>
                                    <span>
                                        {modalMedia.screenAddress}
                                        {modalMedia.city
                                            ? `, ${modalMedia.city}`
                                            : ''}
                                    </span>
                                </div>
                            )}
                            {currentReport.createdAt && (
                                <div className="media-modal__meta-line">
                                    <span>Дата:</span>
                                    <span>
                                        {new Date(
                                            currentReport.createdAt,
                                        ).toLocaleString('ru-RU')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvertisersPage;
